/** Iter 643 — "How it works ▶" video button + full-screen player.
 * Streams from the shared backend via the same-origin /api proxy
 * (GET /api/howto/{id}.mp4|.webm|.jpg — HTTP Range supported). Never a
 * data: URI, never bundled. mp4 unless the browser lacks H.264 but has
 * VP9 (per work order). */
import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";

const TITLES = {
  customer: "How to earn rewards as a customer",
  vendor: "How the Partner Program works",
};

export default function HowItWorksButton({ video, compact = false, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        data-testid={testid || `howto-${video}`}
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold px-4 min-h-[44px] shadow-card transition-colors"
      >
        ▶ How it works{compact ? "" : " · 1½ min video"}
      </button>
      {open && <HowToModal video={video} onClose={() => setOpen(false)} />}
    </>
  );
}

function pickSrc(video) {
  // mp4 first; .webm only when the browser can't do H.264 but can do VP9.
  try {
    const el = document.createElement("video");
    const mp4 = el.canPlayType('video/mp4; codecs="avc1.640028, mp4a.40.2"');
    const webm = el.canPlayType('video/webm; codecs="vp9"');
    if (!mp4 && webm) return `/api/howto/${video}.webm`;
  } catch {
    // default to mp4
  }
  return `/api/howto/${video}.mp4`;
}

function HowToModal({ video, onClose }) {
  const ref = useRef(null);
  const [src] = useState(() => pickSrc(video));
  const [needSound, setNeedSound] = useState(false);
  // Autoplay with sound; browsers that block unmuted autoplay get a muted
  // start + a "Tap for sound" chip. Pause on close (unmount).
  useEffect(() => {
    const el = ref.current;
    el?.play?.().catch(() => {
      if (!el) return;
      el.muted = true;
      el.play().catch(() => {
        // controls are visible — the user can press play
      });
      setNeedSound(true);
    });
    return () => {
      try {
        el?.pause();
      } catch {
        // already gone
      }
    };
  }, []);
  return (
    <div
      className="fixed inset-0 z-[120] bg-black/90 flex flex-col"
      data-testid="howto-modal"
      onClick={onClose}
    >
      <div className="flex items-center justify-between gap-3 px-4 py-3" onClick={(e) => e.stopPropagation()}>
        <div className="text-white text-sm font-bold truncate" data-testid="howto-modal-title">{TITLES[video]}</div>
        <button
          type="button"
          data-testid="video-preview-close"
          onClick={onClose}
          aria-label="Close video"
          className="shrink-0 w-11 h-11 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="relative flex-1 min-h-0 grid place-items-center p-2 pb-6" onClick={(e) => e.stopPropagation()}>
        <video
          ref={ref}
          src={src}
          poster={`/api/howto/${video}.jpg`}
          controls
          autoPlay
          playsInline
          data-testid="howto-video"
          className="h-full max-h-full max-w-full rounded-xl"
        />
        {needSound && (
          <button
            type="button"
            data-testid="howto-unmute"
            onClick={() => {
              const el = ref.current;
              if (el) el.muted = false;
              setNeedSound(false);
            }}
            className="absolute top-4 left-1/2 -translate-x-1/2 rounded-full bg-white text-slate-900 text-xs font-bold px-4 py-2 shadow-card"
          >
            🔊 Tap for sound
          </button>
        )}
      </div>
    </div>
  );
}
